import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import type {
  RateStrategy,
  Rscancellationpolicy,
  Rspattern,
  Rsstrategy,
  Rstype,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { MEALTYPES } from 'lumen/utils/constants/general-constants';
import {
  LABEL_BRANDDOTCOM_WEEKEND,
  RSTYPES_IN_CANCELLATION_POLICY_SECTION,
  SUBTYPE_ID_MOBILE_BOOKINGDOTCOM,
  SUBTYPE_ID_MOBILE_EXPEDIA,
  SUBTYPE_ID_MOBILE_HOTELSDOTCOM,
  SUBTYPE_ID_POS_BOOKINGDOTCOM,
  SUBTYPE_ID_POS_EXPEDIA,
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_PATTERN,
  SUBTYPE_VALUES_KEY_STRATEGY,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  formatCancellationpolicyValue,
  formatStrategyValue,
  formatSubtypeLabel,
} from 'lumen/utils/strategy/format-helper-utils';

export default class Rssubtype extends Model {
  [Type] = 'rssubtype' as const;

  @belongsTo('rssubtype', { async: false, inverse: 'historicEntities' })
  declare latestEntity: Rssubtype;

  @belongsTo('rate-strategy', { async: false, inverse: 'rssubtypes' })
  declare rateStrategy: RateStrategy;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  // Rate strategy subtype corresponds with one "metric card" in a rate strategy type block
  @belongsTo('rstype', { async: false, inverse: 'rssubtypes' })
  declare rstype: Rstype;

  @hasMany('rssubtype', { async: false, inverse: 'latestEntity' })
  declare historicEntities: HasMany<Rssubtype>;

  @hasMany('rscancellationpolicy', { async: false, inverse: 'rssubtype' })
  declare rscancellationpolicies: HasMany<Rscancellationpolicy>;

  @hasMany('rspattern', { async: false, inverse: 'rssubtype' })
  declare rspatterns: HasMany<Rspattern>;

  @hasMany('rsstrategy', { async: false, inverse: 'rssubtype' })
  declare rsstrategies: HasMany<Rsstrategy>;

  @attr('string') declare label: string;
  @attr('string') declare message: string;
  @attr('string') declare name: string;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetMedians() {
    const result: { [key: string]: number | null | undefined } = {
      [SUBTYPE_VALUES_KEY_STRATEGY]: null,
      [SUBTYPE_VALUES_KEY_CANCELLATION_POLICY]: null,
    };

    if (this.hasStrategies) {
      result[SUBTYPE_VALUES_KEY_STRATEGY] = this.calculateStrategyMedian(
        this.filteredStrategies,
      );
    }

    if (this.hasCancellationPolicies) {
      result[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY] =
        this.calculateCancellationPolicyMedian(
          this.filteredCancellationpolicies,
        );
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateWeekOffset() {
    return this.rateStrategy?.extractDateWeekOffset;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCancellationpolicies() {
    return this.rscancellationpolicies.filter(
      (cancellationpolicy) =>
        this.hotelAndMarketService.isOwnHotelInfo(
          cancellationpolicy.hotelinfo,
        ) ||
        this.rsSelections.selectedCompetitorIDs.includes(
          // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
          cancellationpolicy.hotelinfo.id,
        ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredPatterns() {
    return this.rspatterns.filter(
      (pattern) =>
        this.hotelAndMarketService.isOwnHotelInfo(pattern.hotelinfo) ||
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        this.rsSelections.selectedCompetitorIDs.includes(pattern.hotelinfo.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredStrategies() {
    return this.rsstrategies.filter(
      (strategy) =>
        this.hotelAndMarketService.isOwnHotelInfo(strategy.hotelinfo) ||
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        this.rsSelections.selectedCompetitorIDs.includes(strategy.hotelinfo.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCompsetMedians() {
    const strategyMedian = this.compsetMedians[SUBTYPE_VALUES_KEY_STRATEGY];
    const cancellationPolicyMedian =
      this.compsetMedians[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY];

    return {
      [SUBTYPE_VALUES_KEY_STRATEGY]:
        strategyMedian === null || strategyMedian === undefined
          ? this.intl.t('general.missing').toString()
          : formatStrategyValue(
              this.intl,
              this.hotelAndMarketService,
              strategyMedian,
              Boolean(this.filteredStrategies[0]?.isAbsolute),
            ),
      [SUBTYPE_VALUES_KEY_CANCELLATION_POLICY]:
        cancellationPolicyMedian === null ||
        cancellationPolicyMedian === undefined
          ? this.intl.t('general.missing').toString()
          : formatCancellationpolicyValue(this.intl, cancellationPolicyMedian),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLabel() {
    return formatSubtypeLabel(this);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedSubtypePosition() {
    if (this.subtypePosition === -1) {
      return this.intl.t('general.missing').toString();
    }

    return `${this.subtypePosition}/${this.sortedPositionArray.length}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCancellationPolicies() {
    return Boolean(this.filteredCancellationpolicies.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNeutralPosition() {
    // The subtype position is neutral in 2 cases:
    // 1) if we have a subtype position & it's the same as our calculated usual price point
    // 2) if we don't have an own hotel position we can't show any useful value, but we will consider this as a neutral position

    // Note: this check is to detect case 2
    if (this.subtypePosition === -1) {
      return true;
    }

    if (this.hasStrategySubtypePosition) {
      return this.rsSelections.usualPricePoint === this.subtypePosition;
    }

    return null;
  }

  // Check if there is only non-computable data (own hotel not included)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNonComputableDataOnly() {
    const result = {
      [SUBTYPE_VALUES_KEY_STRATEGY]: false,
      [SUBTYPE_VALUES_KEY_CANCELLATION_POLICY]: false,
    };

    if (this.hasStrategies) {
      result[SUBTYPE_VALUES_KEY_STRATEGY] = this.filteredStrategies
        .filter((strategy) => !strategy.isOwnHotel)
        .every((strategy) => strategy.isNonComputable);
    }
    if (this.hasCancellationPolicies) {
      result[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY] =
        this.filteredCancellationpolicies
          .filter((cancellationpolicy) => !cancellationpolicy.isOwnHotel)
          .every((cancellationpolicy) => cancellationpolicy.isNonComputable);
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPatterns() {
    return Boolean(this.filteredPatterns.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasStrategies() {
    return Boolean(this.filteredStrategies.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasStrategySubtypePosition() {
    // Return only subtypes with a strategy and explicitly filter out mealtypes for now since they have a strategy but it shouldn't be displayed
    return this.hasStrategies && this.subtypePosition && !this.isMealtypeType;
  }

  get historicEntity(): Rssubtype | undefined {
    // Use the reference period of the Strategy or Cancellation Policy section depending on the current rstype
    const referencePeriod = RSTYPES_IN_CANCELLATION_POLICY_SECTION.includes(
      this.rstype.name,
    )
      ? this.rsSelections.referencePeriodCancellationPolicySection
      : this.rsSelections.referencePeriodStrategySection;

    return this.historicEntities.find(
      (rsentity) => rsentity.extractDateWeekOffset === referencePeriod,
    );
  }

  get isBestFlexType(): boolean {
    return this.rstype?.isBestFlexType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBranddotcomMemberRatesSubType() {
    return this.isMemberRatesType && this.label.includes(OTA_IDS.BRANDDOTCOM);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationPolicyType() {
    return this.isBestFlexType || this.isSemiFlexType || this.isNonRefType;
  }

  // These subtypes are only visible in the detail views
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExcludedFromSummaryView() {
    // Branddotcom member rates are passed as a seperate rssubtype in the API but should be excluded from the summary view
    return this.isMembershipBranddotcomWeekendSubtype;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHigherThanUsualPricePoint() {
    if (this.hasStrategySubtypePosition) {
      return this.rsSelections.usualPricePoint < this.subtypePosition;
    }

    return null;
  }

  get isLosType(): boolean {
    return this.rstype?.isLosType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMealtypeRoomOnlySubtype() {
    return this.isMealtypeType && this.label === `${MEALTYPES.ROOM_ONLY}`;
  }

  get isMealtypeType(): boolean {
    return this.rstype?.isMealtypeType;
  }

  get isMemberRatesType(): boolean {
    return this.rstype?.isMemberRatesType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMembershipBranddotcomWeekendSubtype() {
    return this.isMemberRatesType && this.label === LABEL_BRANDDOTCOM_WEEKEND;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileBookingSubtype() {
    return this.name.startsWith(SUBTYPE_ID_MOBILE_BOOKINGDOTCOM);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileExpediaSubtype() {
    return this.name.startsWith(SUBTYPE_ID_MOBILE_EXPEDIA);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileHotelsDotComSubtype() {
    return this.name.startsWith(SUBTYPE_ID_MOBILE_HOTELSDOTCOM);
  }

  get isMobileType(): boolean {
    return this.rstype?.isMobileType;
  }

  get isNonRefType(): boolean {
    return this.rstype?.isNonRefType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNotOffered() {
    return this.message === 'rates.notoffered';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosBookingSubtype() {
    return this.name.startsWith(SUBTYPE_ID_POS_BOOKINGDOTCOM);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosExpediaSubtype() {
    return this.name.startsWith(SUBTYPE_ID_POS_EXPEDIA);
  }

  get isPosType(): boolean {
    return this.rstype?.isPosType;
  }

  get isRoomtypeType(): boolean {
    return this.rstype?.isRoomtypeType;
  }

  get isSemiFlexType(): boolean {
    return this.rstype?.isSemiFlexType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelData() {
    const result: { [key: string]: unknown } = {
      [SUBTYPE_VALUES_KEY_STRATEGY]: null,
      [SUBTYPE_VALUES_KEY_CANCELLATION_POLICY]: null,
      [SUBTYPE_VALUES_KEY_PATTERN]: null,
    };

    if (this.hasStrategies) {
      result[SUBTYPE_VALUES_KEY_STRATEGY] = this.filteredStrategies.find(
        (strategy) => strategy.hotelinfo.id === this.ownHotelinfoId,
      );
    }

    if (this.hasCancellationPolicies) {
      result[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY] =
        this.filteredCancellationpolicies.find(
          (cancellationpolicy) =>
            cancellationpolicy.hotelinfo.id === this.ownHotelinfoId,
        );
    }

    if (this.hasPatterns) {
      result[SUBTYPE_VALUES_KEY_PATTERN] = this.filteredPatterns.find(
        (pattern) => pattern.hotelinfo.id === this.ownHotelinfoId,
      );
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelinfoId() {
    return this.hotelAndMarketService.hotel.hotelinfo.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedPositionArray() {
    // We either have a subtypePosition for strategies OR for patterns (never both)
    if (this.hasStrategies) {
      return [...this.filteredStrategies]
        .filter((strategy) => !strategy.isExcludedFromPosition)
        .sort((strategyA, strategyB) => {
          if (
            strategyA.discountedPriceWithFallback >
            strategyB.discountedPriceWithFallback
          ) {
            return -1;
          }
          if (
            strategyB.discountedPriceWithFallback >
            strategyA.discountedPriceWithFallback
          ) {
            return 1;
          }
          // Subsort on hotel name (ascending) in case of equal discounted prices
          return strategyA.hotelinfo.name.localeCompare(
            strategyB.hotelinfo.name,
          );
        });
    }

    if (this.hasPatterns) {
      return [...this.filteredPatterns]
        .filter((pattern) => !pattern.isExcludedFromPosition)
        .sort((patternA, patternB) => {
          if (patternA.rate > patternB.rate) {
            return -1;
          }
          if (patternB.rate > patternA.rate) {
            return 1;
          }
          // Subsort on hotel name (ascending) in case of equal rates
          return patternA.hotelinfo.name.localeCompare(patternB.hotelinfo.name);
        });
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subtypePosition() {
    if (!this.ownHotelinfoId) {
      return -1;
    }

    const ownHotelIndex = this.sortedPositionArray
      .map((item) => item.hotelinfo.id)
      .indexOf(this.ownHotelinfoId);

    // If the own hotel is found in the sortedPositionArray: convert it's (zero-based) index to a position
    if (ownHotelIndex >= 0) {
      return ownHotelIndex + 1;
    }

    return -1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private calculateCancellationPolicyMedian(
    cancellationPolicies: Rscancellationpolicy[],
  ) {
    const filteredCancellationpolicies = cancellationPolicies
      .filter(
        (cancellationpolicy) =>
          !cancellationpolicy.hasNoData && !cancellationpolicy.isOwnHotel,
      )
      .map((cancellationpolicy) => cancellationpolicy.days);

    if (!filteredCancellationpolicies.length) {
      return null;
    }

    return fnMedian(filteredCancellationpolicies);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private calculateStrategyMedian(strategies: Rsstrategy[]) {
    const filteredStrategies = strategies.filter(
      (strategy) => !strategy.hasNoData && !strategy.isOwnHotel,
    );
    if (!filteredStrategies.length) {
      return null;
    }

    const strategiesWithoutZeroValues = filteredStrategies.filter(
      (strategy) => strategy.value !== 0,
    );
    if (strategiesWithoutZeroValues.length) {
      return fnMedian(
        strategiesWithoutZeroValues.map(
          (strategy) => strategy.supplementOrDiscount,
        ),
      );
    }

    return 0;
  }
}
